import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMedicalHistoryMutations } from '@/features/patient-record/hooks/useMedicalHistoryMutations';
import type {
  MeasurementType,
  MeasurementViewModel,
} from '@/features/patient-record/types';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';
import { MeasurementForm } from './MeasurementForm';
import {
  computeBmi,
  formatMeasurement,
  formatMeasurementText,
  UNIT_SYSTEM,
  valueForType,
  type MeasurementDisplay,
  type UnitSystem,
} from './unitConversion';

const MEASUREMENT_COLUMNS: readonly MeasurementType[] = [
  'WEIGHT',
  'HEIGHT',
  'HEAD_CIRCUMFERENCE',
  'ARM_SPAN',
];

interface MeasurementsCardProps {
  readonly guid: string;
  readonly measurements: readonly MeasurementViewModel[];
  /** Patient DOB — passed through to the form so it can auto-compute
   *  the required `ageInMonths` field on every measurement write. */
  readonly dateOfBirth: string | null;
}

/**
 * Pivot view of measurements: rows = date-scoped records (descending),
 * columns = measurement types that actually have data for this patient.
 * A metric/imperial toggle in the card header is display-only — storage
 * stays metric.
 *
 * Add and Edit open the **same modal** — Add opens a blank dialog; a row
 * opens the same dialog prefilled from the chosen record. Row-level delete
 * removes the whole record (all qualifiers at that date).
 */
export function MeasurementsCard({
  guid,
  measurements,
  dateOfBirth,
}: MeasurementsCardProps): ReactElement {
  const { t } = useTranslation();
  const { deleteMeasurement } = useMedicalHistoryMutations(guid);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(UNIT_SYSTEM.metric);
  // 'create' opens a blank form; a record opens the same form prefilled.
  // null means the table is in its quiet state with the Add button.
  const [editing, setEditing] = useState<'create' | MeasurementViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MeasurementViewModel | null>(null);

  const records = [...measurements].sort((a, b) =>
    b.dateMeasured.localeCompare(a.dateMeasured),
  );
  const activeColumns = MEASUREMENT_COLUMNS.filter((type) =>
    measurements.some((m) => m.qualifiers.some((q) => q.measurementType === type)),
  );
  // BMI column is derived — show whenever a record has both weight and
  // height present, regardless of which other types appear.
  const showBmiColumn = measurements.some((m) => {
    const weight = m.qualifiers.find((q) => q.measurementType === 'WEIGHT');
    const height = m.qualifiers.find((q) => q.measurementType === 'HEIGHT');
    return (
      weight !== undefined &&
      height !== undefined &&
      computeBmi(weight.valueKg, height.valueCm) !== null
    );
  });
  // Only render the Notes column if at least one record carries a note —
  // avoids a permanently-empty column on tables that never use them.
  const showNotesColumn = measurements.some(
    (m) => m.notes !== null && m.notes.trim().length > 0,
  );

  // Render the count to the right of the unit toggle (rather than the
  // default left slot in CollapsibleCard) so the toggle sits closer to
  // the title and the count anchors the row end.
  const headerAction = measurements.length > 0 && (
    <div className="flex items-center gap-2">
      <UnitToggle value={unitSystem} onChange={setUnitSystem} />
      <span
        className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-xs font-medium tabular-nums"
        aria-label={`${String(measurements.length)} items`}
      >
        {measurements.length}
      </span>
    </div>
  );

  const editingRecord =
    editing !== null && editing !== 'create' ? editing : undefined;

  // Collapsed summary reflects the most recent record (records[0], since
  // `records` is sorted descending by date) — the "current" measurement
  // snapshot for the patient, one row per column actually shown in the table.
  const latestRecord = records[0];
  const summaryRows: CollapsibleCardSummaryRow[] = activeColumns.map((type) => {
    const cell = latestRecord?.qualifiers.find((q) => q.measurementType === type);
    const numeric = cell !== undefined ? valueForType(cell, type) : null;
    return {
      label: t(`patient.medicalHistory.measurement.type.${type}`),
      value:
        numeric === null ? '—' : formatMeasurementText(numeric, type, unitSystem),
      filled: numeric !== null,
    };
  });
  if (showBmiColumn) {
    const weightCell = latestRecord?.qualifiers.find((q) => q.measurementType === 'WEIGHT');
    const heightCell = latestRecord?.qualifiers.find((q) => q.measurementType === 'HEIGHT');
    const bmi = computeBmi(weightCell?.valueKg ?? null, heightCell?.valueCm ?? null);
    summaryRows.push({
      label: t('patient.medicalHistory.measurement.bmiColumn'),
      value: bmi === null ? '—' : bmi.toFixed(1),
      filled: bmi !== null,
    });
  }

  return (
    <>
      <CollapsibleCard
        idPrefix="medical-history"
        anchorId="measurements"
        title={t('patient.medicalHistory.measurements')}
        defaultOpen
        {...(headerAction ? { headerAction } : {})}
        summary={summaryRows}
      >
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing('create');
          }}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
          {t('patient.medicalHistory.addMeasurement')}
        </Button>

        {measurements.length === 0 ? (
          <p className="text-sm text-muted-foreground italic mt-3">
            {t('patient.medicalHistory.noMeasurements')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border/60 mt-3">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th scope="col" className="text-left font-medium px-2 py-1.5">
                    {t('patient.medicalHistory.measurement.dateColumn')}
                  </th>
                  {activeColumns.map((type) => (
                    <th
                      key={type}
                      scope="col"
                      className="text-right font-medium px-2 py-1.5"
                    >
                      {t(`patient.medicalHistory.measurement.type.${type}`)}
                    </th>
                  ))}
                  {showBmiColumn && (
                    <th scope="col" className="text-right font-medium px-2 py-1.5">
                      {t('patient.medicalHistory.measurement.bmiColumn')}
                    </th>
                  )}
                  {showNotesColumn && (
                    <th
                      scope="col"
                      className="text-left font-medium px-2 py-1.5 w-[12rem]"
                    >
                      {t('patient.medicalHistory.measurement.notesColumn')}
                    </th>
                  )}
                  <th scope="col" className="px-2 py-1.5 w-[1%]">
                    <span className="sr-only">{t('patient.medicalHistory.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {records.map((record) => {
                  const isEditingThis = editingRecord?.id === record.id;
                  const weightCell = record.qualifiers.find(
                    (q) => q.measurementType === 'WEIGHT',
                  );
                  const heightCell = record.qualifiers.find(
                    (q) => q.measurementType === 'HEIGHT',
                  );
                  const bmi = computeBmi(
                    weightCell?.valueKg ?? null,
                    heightCell?.valueCm ?? null,
                  );
                  return (
                    <tr
                      key={record.id}
                      className={
                        isEditingThis ? 'bg-muted/30' : 'hover:bg-muted/20'
                      }
                    >
                      <td className="px-2 py-1.5 tabular-nums whitespace-nowrap">
                        {formatDate(record.dateMeasured)}
                      </td>
                      {activeColumns.map((type) => {
                        const cell = record.qualifiers.find(
                          (q) => q.measurementType === type,
                        );
                        const numeric =
                          cell !== undefined ? valueForType(cell, type) : null;
                        return (
                          <td
                            key={type}
                            className="px-2 py-1.5 text-right tabular-nums"
                          >
                            {cell === undefined || numeric === null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <MeasurementCellValue
                                display={formatMeasurement(numeric, type, unitSystem)}
                                onEdit={() => {
                                  setEditing(record);
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                      {showBmiColumn && (
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {bmi === null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            bmi.toFixed(1)
                          )}
                        </td>
                      )}
                      {showNotesColumn && (
                        <td className="px-2 py-1.5 text-muted-foreground w-[12rem] max-w-[12rem]">
                          {record.notes !== null && record.notes.trim().length > 0 ? (
                            <NoteCell text={record.notes} />
                          ) : (
                            <span className="text-muted-foreground/70">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <RowActions
                          onEdit={() => {
                            setEditing(record);
                          }}
                          onDelete={() => {
                            setPendingDelete(record);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleCard>

      {editing !== null && (
        <MeasurementForm
          guid={guid}
          dateOfBirth={dateOfBirth}
          unitSystem={unitSystem}
          {...(editingRecord !== undefined ? { record: editingRecord } : {})}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.medicalHistory.deleteConfirmTitle')}
          message={t('patient.medicalHistory.deleteConfirmMessage')}
          confirmLabel={t('patient.medicalHistory.deleteMeasurement')}
          busy={deleteMeasurement.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            deleteMeasurement.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

interface MeasurementCellValueProps {
  readonly display: MeasurementDisplay;
  readonly onEdit: () => void;
}

/** Table cell value — the number carries the unit symbol muted beside it,
 *  except in feet/inches where the text already reads as 5′ 11.7″. */
function MeasurementCellValue({
  display,
  onEdit,
}: MeasurementCellValueProps): ReactElement {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onEdit}
      className="hover:text-primary transition-colors"
      aria-label={t('patient.medicalHistory.editMeasurement')}
    >
      {display.text}
      {display.unit !== null && (
        <span className="text-muted-foreground ml-1">{display.unit}</span>
      )}
    </button>
  );
}

interface RowActionsProps {
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

/**
 * Single-line note preview inside the table. Long notes are clipped
 * with an ellipsis; hovering surfaces the full text in a tooltip with
 * `break-words` so very long unbroken tokens still wrap there.
 */
function NoteCell({ text }: { readonly text: string }): ReactElement {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block truncate">{text}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap break-words">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RowActions({ onEdit, onDelete }: RowActionsProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={onEdit}
        aria-label={t('patient.medicalHistory.editMeasurement')}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onDelete}
        aria-label={t('patient.medicalHistory.deleteMeasurement')}
      >
        <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
      </Button>
    </div>
  );
}

interface UnitToggleProps {
  readonly value: UnitSystem;
  readonly onChange: (next: UnitSystem) => void;
}

function UnitToggle({ value, onChange }: UnitToggleProps): ReactElement {
  const { t } = useTranslation();
  const options: readonly { id: UnitSystem; label: string }[] = [
    { id: UNIT_SYSTEM.metric, label: t('patient.medicalHistory.metricShort') },
    { id: UNIT_SYSTEM.imperial, label: t('patient.medicalHistory.imperialShort') },
  ];
  return (
    <div
      role="group"
      aria-label={t('patient.medicalHistory.units')}
      className="inline-flex rounded-md border border-border bg-card p-0.5 text-[11px]"
    >
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            onClick={() => {
              onChange(opt.id);
            }}
            className={cn(
              'px-2.5 py-0.5 rounded-sm font-medium transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
